/** Request context for debugging API errors */
export interface ScenarioRequestContext {
  modelId?: string;
  modelName?: string;
  endpoint?: string;
  params?: Record<string, unknown>;
}

/** Base error for all Scenario API errors */
export class ScenarioAPIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: unknown,
    public requestContext?: ScenarioRequestContext
  ) {
    super(message);
    this.name = 'ScenarioAPIError';
    Object.setPrototypeOf(this, ScenarioAPIError.prototype);
  }

  /** Get a formatted error message with context */
  getDetailedMessage(): string {
    const parts = [this.message];
    if (this.requestContext?.modelName) parts.push(`Model: ${this.requestContext.modelName}`);
    if (this.requestContext?.modelId) parts.push(`Model ID: ${this.requestContext.modelId}`);
    if (this.requestContext?.endpoint) parts.push(`Endpoint: ${this.requestContext.endpoint}`);
    if (this.statusCode) parts.push(`Status: ${this.statusCode}`);
    return parts.join('\n');
  }
}

/** Error when a job times out during polling */
export class ScenarioJobTimeoutError extends Error {
  constructor(
    public jobId: string,
    public attempts: number
  ) {
    super(`Job ${jobId} timed out after ${attempts} polling attempts`);
    this.name = 'ScenarioJobTimeoutError';
    Object.setPrototypeOf(this, ScenarioJobTimeoutError.prototype);
  }
}

/** Error when an invalid job ID is provided */
export class ScenarioInvalidJobIdError extends Error {
  constructor(public jobId: string | undefined) {
    super(`Invalid job ID: ${jobId}`);
    this.name = 'ScenarioInvalidJobIdError';
    Object.setPrototypeOf(this, ScenarioInvalidJobIdError.prototype);
  }
}

/** Error when attempting to add a duplicate tag (often expected, not critical) */
export class ScenarioDuplicateTagError extends Error {
  constructor(
    public assetId: string,
    public tag: string
  ) {
    super(`Asset ${assetId} already has tag: ${tag}`);
    this.name = 'ScenarioDuplicateTagError';
    Object.setPrototypeOf(this, ScenarioDuplicateTagError.prototype);
  }
}

/** Error when a job is already being polled */
export class ScenarioJobAlreadyPollingError extends Error {
  constructor(public jobId: string) {
    super(`Job ${jobId} is already being polled`);
    this.name = 'ScenarioJobAlreadyPollingError';
    Object.setPrototypeOf(this, ScenarioJobAlreadyPollingError.prototype);
  }
}

/**
 * Check if an error is a duplicate tag error
 */
export function isDuplicateTagError(error: any): boolean {
  if (error instanceof ScenarioDuplicateTagError) {
    return true;
  }

  // Check error message patterns
  const errorMessage = error?.message?.toLowerCase() ?? '';
  return errorMessage.includes('duplicated tag') || errorMessage.includes('duplicate tag');
}

/**
 * Check if an error is a 500 server error that should be retried
 */
export function isRetryableServerError(error: any): boolean {
  if (error instanceof ScenarioAPIError) {
    return error.statusCode === 500;
  }

  const errorMessage = error?.message ?? '';
  return errorMessage.includes('500') || errorMessage.includes('Internal Server Error');
}

/**
 * Check if an error is a 4xx client error (should not be retried)
 */
export function isClientError(error: any): boolean {
  if (error instanceof ScenarioAPIError && error.statusCode) {
    return error.statusCode >= 400 && error.statusCode < 500;
  }

  return false;
}

/**
 * Parse error response and create appropriate error type
 */
export function parseAPIError(
  statusCode: number,
  errorText: string,
  response?: unknown,
  requestContext?: ScenarioRequestContext
): ScenarioAPIError {
  // Check for duplicate tag error
  if (statusCode === 400 && errorText.includes('Duplicated tag')) {
    // Extract asset ID and tag if possible (parse from error text)
    return new ScenarioAPIError(errorText, statusCode, response, requestContext);
  }

  return new ScenarioAPIError(errorText, statusCode, response, requestContext);
}

/**
 * Determine if an error should trigger a retry
 */
export function shouldRetry(error: any, attempt: number, maxRetries: number): boolean {
  if (attempt >= maxRetries) {
    return false;
  }

  // Only retry on server errors (500)
  return isRetryableServerError(error);
}

